<template lang="pug">
.f-scale-table
  p {{ __('April 29, 2024') }}
  .title-container
    .logo-and-title-container
      img.logo(alt="The F Scale" src="https://assets.roguefitness.com/image/upload/v1712155001/theindex/Index%20Post%20Media/TheFscaleLogo_gleqzr.svg" :width="50" :height="30")
      span.title {{ __('BARBELL DURABILITY RATING TEST') }}
    .clear-filter-container
      button.clear-filters.btn-empty(@click="clearAllFilters")
        span.clear-filters-text {{ __('Clear All Filters') }}
    Modal(v-if="openModal" @close="openModal = false" :video="true" :width="theme === 'vertical' ? '380px' : null" @youTubeReady="youTubePlayer = $event")
      .video
        YouTubeVideo(:videoId="rowVideoId" aspectRatio="none" :class="[theme === 'vertical' ? 'videos' : 'video']")
  .table-container
    table.no-desc
      thead
        tr
          th.header(v-for="(header, index) in headers" :key="index" @click="toggleSearchCriteria(header, index)")
            .title-header-container
              .header-title(:class="header === 'Brand' ? 'left' : 'center'")
                span.filter-name(v-html="header" v-translate)
                span.active-count(v-if="activeFilterOptions[index].filter(val => val).length !== 0") &nbsp;{{ `(${activeFilterOptions[index].filter(val => val).length})` }}
              .filter-container
                Icon.header-icon(name='small-hamburger')
            .search-box(v-if="!isMobile" v-show="searchBoxVisible === header" @click.stop :class="index > headers.length - 6 ? 'position-right' : 'position-left'")
              .a-to-z(v-if="sortByLetters && selectedHeader !== 'Diameter' & selectedHeader !== 'Tensile' & selectedHeader !== 'Category'")
                checkbox.sort(type="checkbox" label="Sort A to Z" v-model="pendingSortAscending[index]")
                checkbox.sort(type="checkbox" label="Sort Z to A" v-model="pendingSortDescending[index]")
              .high-to-low(v-else)
                checkbox.sort(type="checkbox" label="High to Low" v-model="pendingSortDescending[index]")
                checkbox.sort(type="checkbox" label="Low to High" v-model="pendingSortAscending[index]" )
              hr(v-if="sortByLetters")
              .search-field(v-if="dontHideSearchBar")
                RogueInput(type="text" v-model="searchBoxText")
                .search-btn
                  Icon(name="search")
              .clear-filters-link
                i {{ __('All results shown unless filtered. ') }}
                span.clear-filters-text(@click="clearFiltersWithoutClosing(index)") {{ __(' Clear Filters') }}
              div(v-for="(option, filterIndex) in allOptions[index]")
                checkbox.search-options(
                  v-if="sortByLetters && filteredOptions[index].includes(allOptions[index][filterIndex])"
                  type="checkbox"
                  :label="option.toString()"
                  v-model="pendingFilterOptions[index][filterIndex]"
                  :key="filterIndex")
              hr
              .button-container
                RogueButton(@click="cancelSearch()") {{ __('Cancel') }}
                RogueButton(type="button" color="black" @click="submitSortAndFilter(index)" :disabled="!applyButtonActive(index)" ) {{ __('Apply') }}
      tbody
        tr.highlight-row(v-for="(row, rowIndex) in filteredAndSortedResults" :key="rowIndex")
          td.result-container(v-for="(header, index) in headers" :key="index")
            .result
              span(v-if="header === 'Made </br> In'")
                Icon.flag(:name="flagMap[row[header]]" :class="row[header] === 'Japan' ? 'bordered' : ''")
              span(v-else).brand-text
                RogueLink(v-if="header === 'Brand' && row.url" :to="row.url" target="_blank")
                  img(src="https://assets.roguefitness.com/image/upload/v1593548025/cms/brand/rogue-logo-black_ffulmp.svg" alt="Rogue")
                span(v-else-if="row[header] === 'Yes'")
                  span <strong>{{ row[header] }}</strong>
                span(v-else) {{ formatData(row[header], header) }}
              span(v-if="header === 'Brand' && row.video && showVideoFeature")
                button.btn-empty(@click="selectVideo(row.video)")
                  Icon.play(name='play' fill='black')
        tr(v-if="filteredAndSortedResults.length === 0")
          td.center-text(:colspan="headers.length") {{ __('No Data Matches These Filters') }}
  .note
    div
      strong {{ __('* Note: ') }}
      ul.notes
        li {{ __('We were unable to get the stainless RWH samples to fail in our test.') }}
        li {{ __('All barbells were acquired and tested in 2024') }}

    Drawer(v-if="isMobile && openHeaderIndex >= 0" title="Sort & Filter Options" :overlayClickClosesDrawer='false' @hide="cancelSearch()")
      .search-box
        .a-to-z(v-if="sortByLetters && selectedHeader !== 'Diameter' & selectedHeader !== 'Tensile' & selectedHeader !== 'Category'")
          checkbox.sort(type="checkbox" label="Sort A to Z" v-model="pendingSortAscending[openHeaderIndex]")
          checkbox.sort(type="checkbox" label="Sort Z to A" v-model="pendingSortDescending[openHeaderIndex]")
        .high-to-low(v-else)
          checkbox.sort(type="checkbox" label="High to Low" v-model="pendingSortDescending[openHeaderIndex]")
          checkbox.sort(type="checkbox" label="Low to High" v-model="pendingSortAscending[openHeaderIndex]" )
        hr(v-if="sortByLetters")
        .search-field(v-if="dontHideSearchBar")
          RogueInput(type="text" v-model="searchBoxText")
          .search-btn
            Icon(name="search")
        .clear-filters-link
          i {{ __('All results shown unless filtered. ') }}
          span.clear-filters-text(@click="clearFiltersWithoutClosing(openHeaderIndex)") {{ __(' Clear Filters') }}
        div(v-for="(option, filterIndex) in allOptions[openHeaderIndex]")
          checkbox.search-options(
            v-if="sortByLetters && filteredOptions[openHeaderIndex].includes(allOptions[openHeaderIndex][filterIndex])"
            type="checkbox"
            :label="option.toString()"
            v-model="pendingFilterOptions[openHeaderIndex][filterIndex]"
            :key="filterIndex")
        hr
        .button-container
          RogueButton(@click="cancelSearch()") {{ __('Cancel') }}
          RogueButton(type="button" color="black" @click="submitSortAndFilter(openHeaderIndex)" :disabled="!applyButtonActive" ) {{ __('Apply') }}
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted } from 'vue'
import { FSCALE_DATA, type IFScaleData } from '../data'

const results = FSCALE_DATA
const flagMap = {
  US     : 'flags/US',
  China  : 'flags/CN',
  Sweden : 'flags/SE',
  Japan  : 'flags/JP',
}
let mediaQuery: any

const isMobile = ref(true)
const rowVideoId = ref('5TKTMsuIfgA')
const openModal = ref(false)
const searchBoxVisible = ref('')
const dontHideSearchBar = ref(true)
const headers = computed(() => Object.keys(results[0]).filter(k => !['video', 'url'].includes(k)))
const openHeaderIndex = computed(() => headers.value.findIndex(header => header === searchBoxVisible.value))
const sortByLetters = ref(true)
const filteredAndSortedResults = ref<IFScaleData[]>([])
const searchBoxText = ref('')
const selectedHeader = ref('Brand')
const activeSortAscending = ref(false)
const activeSortDescending = ref(false)
const activeSortIndex = ref(0)
// @todo delete and remove from v-if once videos are released; find solution to YT positioning without changing inline styles in the YT component
const showVideoFeature = ref(false)
const pendingSortAscending = ref(Array(headers.value.length).fill(false))
const pendingSortDescending = ref(Array(headers.value.length).fill(false))
function uniqueVals(array: (string | number)[]) {
  return array.filter((item, index) => item !== undefined && item !== null && array.indexOf(item) === index)
}
const filteredOptions = computed(() => headers.value.map((header, index) => {
  const searchInput = searchBoxText.value.trim().toLowerCase()

  if (headers.value[index]) {
    return uniqueVals(results.map(row => row[header]).filter(value => value.toLocaleString().toLowerCase().includes(searchInput)))
  }

  return []
}))
const allOptions = computed(() => headers.value.map((header) => uniqueVals(results.map(row => row[header]))))
const activeFilterOptions = ref<boolean[][]>(allOptions.value.map((column) => column.map(() => false)))
const pendingFilterOptions = ref<boolean[][]>(allOptions.value.map((column) => column.map(() => false)))

function handleResize() {
  isMobile.value = mediaQuery.matches
}

function formatData(value: any, header: string): string | number {
  switch (header) {
    case 'High Repetition - </br> CrossFit Affiliate':
    case 'OLY Gym':
    case 'Garage </br> Gym':
    case 'Power </br> lifting Gym':

      return value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
      })
    case 'Diameter':
    case 'F Rating':

      return value.toFixed(2)
    default:

      return value.toLocaleString()
  }
}

function filterResults() {
  const searchFilteredArray = [...results]
  activeFilterOptions.value.forEach((col, colIndex) => col.forEach((val, index) => col[index] = pendingFilterOptions.value[colIndex][index]))
  activeSortAscending.value = pendingSortAscending.value[activeSortIndex.value]
  activeSortDescending.value = pendingSortDescending.value[activeSortIndex.value]

  // Filtering logic
  const activeFilters = activeFilterOptions.value.map((column, colIndex) => ({
    name   : headers.value[colIndex],
    values : column.map((value, filterIndex) => allOptions.value[colIndex][filterIndex]).filter((val, index) => !!column[index]),
  })).filter(col => col.values.length)

  const filteredArray = searchFilteredArray.filter(row => activeFilters.every(filter => filter.values.includes(row[filter.name])))

  const sortedArray = [...filteredArray]

  // Sorting logic
  headers.value.forEach((header, index) => {
    if (index === activeSortIndex.value && (activeSortAscending.value || activeSortDescending.value)) {
      sortedArray.sort((a, b) => {
        const valueA = a[header]
        const valueB = b[header]

        const numericValueA = typeof valueA === 'number' ? valueA : parseFloat(valueA)
        const numericValueB = typeof valueB === 'number' ? valueB : parseFloat(valueB)

        // Handle cases where valueA or valueB couldn't be converted to numbers
        if (Number.isNaN(numericValueA) || Number.isNaN(numericValueB)) {
          if (activeSortAscending.value) {
            return String(valueA).localeCompare(String(valueB))
          }

          if (activeSortDescending.value) {
            return String(valueB).localeCompare(String(valueA))
          }
        }

        if (activeSortDescending.value) {
          return numericValueB - numericValueA
        }

        if (activeSortAscending.value) {
          return numericValueA - numericValueB
        }

        return numericValueB - numericValueA
      })
    }
  })

  filteredAndSortedResults.value = sortedArray
}
// Calling the function so data renders on first load instead of putting it in both onMounted and onServerPrefetch
filterResults()

function applyButtonActive(column: number) {
  if (activeSortIndex.value === column) {
    return pendingSortAscending.value[column] !== activeSortAscending.value
      || pendingSortDescending.value[column] !== activeSortDescending.value
      || pendingFilterOptions.value[column].some((option, index) => option !== activeFilterOptions.value[column][index])
  }

  return pendingSortAscending.value[column] || pendingSortDescending.value[column]
    || pendingFilterOptions.value[column].some((option, index) => option !== activeFilterOptions.value[column][index])
}

function selectVideo(videoId: string) {
  rowVideoId.value = videoId
  openModal.value = true
}

function clearAllFilters() {
  searchBoxText.value = ''

  activeSortIndex.value = 0

  searchBoxVisible.value = ''

  pendingFilterOptions.value = allOptions.value.map(column => column.map(() => false))
  pendingSortDescending.value.fill(false)
  pendingSortAscending.value.fill(false)

  filterResults()
}

function clearPendingSort() {
  pendingSortDescending.value.fill(false)
  pendingSortAscending.value.fill(false)
  pendingSortAscending.value[activeSortIndex.value] = activeSortAscending.value
  pendingSortDescending.value[activeSortIndex.value] = activeSortDescending.value
}

function clearFiltersWithoutClosing(headerIndex: number) {
  clearPendingSort()
  pendingSortAscending.value[headerIndex] = false
  pendingSortDescending.value[headerIndex] = false
  searchBoxText.value = ''
  pendingFilterOptions.value.forEach((col, colIndex) => col.forEach((val, index) => col[index] = activeFilterOptions.value[colIndex][index]))

  pendingFilterOptions.value[headerIndex].fill(false)
  filterResults()
}

function cancelSearch() {
  clearPendingSort()
  searchBoxVisible.value = ''
  pendingFilterOptions.value.forEach((col, colIndex) => col.forEach((val, index) => col[index] = activeFilterOptions.value[colIndex][index]))
}

function submitSortAndFilter(index: number) {
  activeSortIndex.value = index
  filterResults()
  clearPendingSort()
  searchBoxVisible.value = ''
}

function toggleSearchCriteria(header: string, index: number) {
  cancelSearch()

  if (searchBoxVisible.value === header) {
    searchBoxVisible.value = ''
  } else {
    searchBoxVisible.value = header
  }

  searchBoxText.value = ''

  switch (header) {
    case 'Brand':
    case 'RWH':
      dontHideSearchBar.value = false
      sortByLetters.value = true
      selectedHeader.value = header

      return header
    case 'Made </br> In':
    case 'Finish Type':
    case 'Diameter':
    case 'Category':
    case 'Tensile':
      dontHideSearchBar.value = true
      sortByLetters.value = true
      selectedHeader.value = header

      return header
    default:
      dontHideSearchBar.value = false
      sortByLetters.value = false
      selectedHeader.value = header

      return header
  }
}

watch(pendingSortAscending, () => {
  pendingSortDescending.value[openHeaderIndex.value] = false
}, { deep: true })

watch(pendingSortDescending, () => {
  pendingSortAscending.value[openHeaderIndex.value] = false
}, { deep: true })

onMounted(() => {
  mediaQuery = window.matchMedia('(max-width: 767px)')
  mediaQuery.addListener(handleResize)
  handleResize()

  // Default sorting by F Rating
  pendingSortDescending.value[7] = true
  submitSortAndFilter(7)
})

</script>

<style lang='scss' scoped>

$f-scale-border: 1px solid $color-gray-light;
$filter-icon-height: 7px;
$table-padding: 5px;

.f-scale-table{
  margin: 0 5%;
  margin-bottom: 50px;

  @media (min-width: 2000px) {
    margin: 0 auto 50px auto;
    max-width: 1750px;
  }
}

.flag {
  width: 24px;
  height: 14px;

  &.bordered {
    outline: 1px solid $color-gray-light;
    background-color: #ffffff;
  }
}

.table-container {
  min-height: 800px;
  max-height: 85vh;
  overflow-y: auto;
}

table {
  overflow: visible;

  img {
    height: 10px;
  }

  thead {
    position: sticky;
    top: 0px;
    background-color: #ffffff;
    z-index: 2;
  }

  th.title-cell {
    border: none;
    padding: 0;
  }

  .title-header-container {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    min-height: 65px;
  }

  th.header {
    border: 1px solid black;
    text-align: left;
    cursor: pointer;
    padding: $table-padding;
    vertical-align: top;
    position: relative;
    z-index: 2;

    // Specific header sizes adjusted per UX specs
    &:nth-child(2) {
      width: 120px;
      min-height: 98px;

    }

    &:nth-child(3) {
      min-width: 140px;
    }

    &:nth-child(7) {
      min-width: 60px;
    }

    &:nth-child(9) {
      min-width: 80px;
    }

    &:nth-child(10) {
      min-width: 120px;
      max-width: 140px;
    }

    &:nth-child(11) {
      max-width: 80px;
    }

    &:nth-child(12) {
      width: 80px;
    }

    &:nth-child(13) {
      width: 100px;
    }

    .header-title {
      text-transform: uppercase;
      flex-grow: 1;

      &.left {
        text-align: start;
      }
    }

    .header-icon {
      height: $filter-icon-height;
    }

    .filter-container {
      bottom: $table-padding;
      padding-right: calc($table-padding - 2px);
      position: absolute;
      right: 0;
      bottom: 10px;

      @include tablet-and-small-desktop {
        bottom: 5px;
      }

      @include mobile {
        bottom: 5px;
      }
    }
  }

  td {
    text-align: left;
    white-space: nowrap;
    padding: 8px $table-padding;

    @include not-mobile {
      font-size: 16px;
    }
  }
}

.title-container {
  padding-bottom: $space-s;
  display: flex;
  align-items: center;
  z-index: 3;

  @include mobile {
    align-items: flex-start;
    flex-direction: column;
  }
}

.logo-and-title-container {
  display: flex;

  @include mobile {
    align-items: center;
  }

  .logo {
    width: 75px;
    height: 68px;
  }
}

.clear-filter-container {
  margin-left: auto;

  @include mobile {
    margin-top: $space-s;
    margin-left: 0;
    align-self: flex-start;
  }
}

.title {
  font-size: $size-xxl;
  text-transform: uppercase;
  font-weight: bold;
  padding-left: 10px;
  align-self: center;

  @include mobile {
    width: 260px;
    text-align: start;
    padding-left: 5px;
  }
}

.clear-filters-text {
  color: $color-blue;
  text-decoration: underline;
  cursor: pointer;
  text-align: right;
  margin-left: auto;

  &:hover {
    color: $color-blue-dark;
  }
}

.btn-empty {
  background-color: transparent;
  border: none;
  outline: none;
}

// Width of the sort checkboxes plus padding and margins
$search-indent: 31px;

.search-box {
  color: black;
  cursor: auto;
  text-transform: none;
  font-size: $size-xs;
  font-weight: normal;
  padding: 5px 0;
  width: 400px;
  border: $f-scale-border;
  position: absolute;
  background-color: white;
  z-index: 3;
  top: 75px;
  box-shadow: 0px 2px 6px 0px rgb(233, 233, 233);

  @media (min-width: 1365px) and (max-width: 1569px) {
    top: 85px;
  }

  @include tablet-and-small-desktop {
    top: 85px;
  }

  @include not-desktop {
    width: 350px;
  }

  @include mobile {
    border: none;
    box-shadow: none;
    width: 100%;
  }

  &.position-left {
    left: 0;
    right: auto;
  }

  &.position-right {
    right: 0;
    left: auto;
  }

  .sort {
    padding: 5px;
  }

  hr {
    border: none;
    border-bottom: $f-scale-border;
  }
}

.clear-filters-link, .search-field, hr, .button-container {
  margin-left: $search-indent !important; // important tag is needed for the hr styling
  margin-right: $size-l !important;
}

.clear-filters-link, .clear-sort-link {
  margin-bottom: 10px;
  margin-top: 10px;
  display: flex;
}

.clear-sort-link {
  margin-top: 10px;
}

.search-field {
  position: relative;
}

.search-btn {
  position: absolute;
  top: 0;
  right: 0;
  padding: $space-xs $space-xxs;
  cursor: pointer;
}

.play {
  cursor: pointer;
}

.search-options {
  padding: 5px $size-l 5px $search-indent;
}

.search-options:hover, .highlight-row:hover, .sort:hover {
  background-color: $color-gray-light;
}

.result-container {
  border: $f-scale-border;
}

.result-container .result {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.result-container:nth-child(2) {
  width: 100px;

  .brand-text {
    align-self: center;
    margin-right: auto;
  }
}

.center-text {
  text-align: center;
}

.button-container {
  display: flex;
  margin-bottom: 10px;
  gap: 10px;

  @include mobile{
    flex-direction: column;
    gap: 7px;
  }

  Button {
    flex-grow: 1;

    &:nth-child(1) {
      @include mobile {
        order: 2;
      }
    }
  }
}
.notes {
  list-style-type: none;
}
.note {
  margin-top: $space-xs;
}
</style>
